import { useState } from "react";
import { Play, Clock, X } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";

/**
 * VideoIntro — poster+play que abre modal com o vídeo.
 *
 * Props:
 *  - poster (url), title, duration (ex: "30s"), badge (ex: "Para candidatos")
 *  - videoUrl (mp4 direto)  OU  embedUrl (YouTube/Vimeo iframe src)
 *  - se ambos vazios → estado "Em preparação"
 *
 * Env vars sugeridas (podem ser preenchidas pelo utilizador em frontend/.env):
 *   REACT_APP_VIDEO_CANDIDATE_URL, REACT_APP_VIDEO_COMPANY_URL
 */
export function VideoIntro({ poster, title, duration = "30s", badge, videoUrl, embedUrl, testId }) {
  const [open, setOpen] = useState(false);
  const hasVideo = Boolean(videoUrl || embedUrl);

  return (
    <>
      <button
        type="button"
        data-testid={testId || "video-intro"}
        onClick={() => hasVideo && setOpen(true)}
        disabled={!hasVideo}
        className="group relative w-full overflow-hidden rounded-2xl border border-black/5 shadow-sm bg-slate-900 text-left disabled:cursor-default"
      >
        <div className="aspect-video w-full relative">
          <img src={poster} alt={title}
            className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-95 group-hover:scale-[1.02] transition-all duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/30 to-transparent" aria-hidden="true" />

          {/* Badge topo */}
          {badge && (
            <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-800">{badge}</span>
            </div>
          )}

          {/* Botão play central */}
          <div className="absolute inset-0 flex items-center justify-center">
            {hasVideo ? (
              <div className="flex items-center justify-center w-20 h-20 rounded-full bg-amber-400 shadow-2xl group-hover:scale-110 group-hover:bg-amber-300 transition-all duration-300">
                <Play className="w-8 h-8 text-slate-950 ml-1" fill="currentColor" />
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-full bg-white/95 backdrop-blur px-5 py-2.5 shadow-lg">
                <Clock className="w-4 h-4 text-slate-800" />
                <span className="text-sm font-bold text-slate-900">Vídeo em preparação</span>
              </div>
            )}
          </div>

          {/* Título/duração rodapé */}
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <p className="font-heading font-black tracking-tighter text-lg sm:text-xl">{title}</p>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-300 mt-1">
              {duration} {hasVideo ? "· Ver agora" : "· Publicação próxima"}
            </p>
          </div>
        </div>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl p-0 bg-black border-0 overflow-hidden">
          <button onClick={() => setOpen(false)}
            className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors"
            aria-label="Fechar">
            <X className="w-4 h-4 text-slate-900" />
          </button>
          <div className="aspect-video w-full bg-black">
            {embedUrl ? (
              <iframe src={embedUrl} title={title} className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen />
            ) : videoUrl ? (
              <video src={videoUrl} controls autoPlay className="w-full h-full" poster={poster} />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
